import assetUtils, { Asset } from 'app-lib/assetUtils'
import lendUtils from 'app-lib/lendUtils'
import numberUtils, { abbreviate } from 'app-lib/numberUtils'
import { chainConfigs, isChainSupported } from 'bzx-common/src/config/appConfig'
import { LendType } from 'bzx-common/src/domain/lendTypes'
import { RootStore } from 'bzx-common/src/stores'
import { debounce, DebouncedFunc } from 'lodash'
import * as mobx from 'mobx'
import { FormEvent } from 'react'
import { DialogVM } from 'ui-framework'
import { BigNumber } from '@0x/utils'
import WrapNativeFormVM from './WrapNativeFormVM'

interface ILendForm {
  freeSupply: BigNumber
  inputLendAsset: Asset
  inputLendAmount: string
  isExpired: boolean
  isLoading: boolean
  iTokenAddress: string
  lendedAmountEstimate: BigNumber
  lendType: LendType
  maxLendAmount: BigNumber
  maxTokenAmount: BigNumber
  needsApproval: boolean
  nextSupplyRate?: BigNumber
  spendingApprovalPending: boolean
  tokenPrice: BigNumber
  userLentBalance: BigNumber
  wrapNativeForm: WrapNativeFormVM
}

export default class LendForm implements ILendForm {
  /** How many tokens are available for borrowing or unlending */
  freeSupply = new BigNumber(0)
  inputLendAsset: Asset
  inputLendAmount = ''
  isExpired = false // TODO
  isLoading = true
  iTokenAddress = ''
  lendDialog: DialogVM
  /** How many iTokens the user would receive for lending. Note: we removed this from the UI. Needs rethink */
  lendedAmountEstimate = new BigNumber(0)
  lendType: LendType
  /** For lend and unlend; maximum the user can lend or unlend */
  maxLendAmount = new BigNumber(0)
  /** For unlend, maximum amount the user can unlend, depends on supply and token price */
  maxTokenAmount = new BigNumber(0)
  rootStore: RootStore
  spendingApprovalPending = false
  /** Price of the iToken compared to underlying: how many ETH you get for 1 iETH */
  tokenPrice = new BigNumber(0)
  /** How much the user has lent (in underlying) */
  userLentBalance = new BigNumber(0)
  wrapNativeForm: WrapNativeFormVM
  /** For chains with new interest rate model; updates on user input changes */
  nextSupplyRate?: BigNumber
  refreshSupplyRateDebounced: DebouncedFunc<(supplyAmount: BigNumber) => Promise<void>>
  stopAutoUpdate?: mobx.IReactionDisposer

  get isLending() {
    return this.lendType === 'LEND'
  }

  /**
   * For the select input, we allow user to use the native token or its ERC20 wrapped version
   */
  get selectableAssets() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId || !isChainSupported(chainId)) {
      return []
    }
    const { asset, wrapped } = chainConfigs[chainId].native
    if (![asset, wrapped].includes(this.inputLendAsset)) {
      return [this.inputLendAsset]
    }
    return [asset, wrapped]
  }

  get lendAmountAsBN() {
    return new BigNumber(this.inputLendAmount)
  }

  get insufficientLiquidityToUnlend() {
    if (this.lendType === 'LEND' || this.lendAmountAsBN.isNaN()) {
      return false
    }
    return this.freeSupply.lt(this.lendAmountAsBN)
  }

  /**
   * This is basically for the slider percentage value
   */
  get ratioMaxLendAmount() {
    return this.maxLendAmount.isZero()
      ? 0
      : new BigNumber(this.inputLendAmount).div(this.maxLendAmount).toNumber()
  }

  get warningMessage() {
    if (this.isLoading) {
      return ''
    }
    const { isConnected } = this.rootStore.web3Connection
    return !isConnected
      ? 'Connect your wallet'
      : this.insufficientLiquidityToUnlend
      ? `Free supply is ${abbreviate(this.freeSupply)} ${this.inputLendAsset} - Unlending limited. `
      : this.maxLendAmount.isZero()
      ? `You do not have ${this.inputLendAsset} in your wallet`
      : this.isExpired
      ? 'Price has changed'
      : ''
  }

  get assetSelectedInfo() {
    const { lendStore, chain } = this.rootStore
    if (!chain) {
      return
    }
    // Note: this can be confusing for native / wrapped tokens;
    // wrapped token is not present in the list and relies on native info
    const searchAsset =
      !chain.nativeTokenSupported && this.inputLendAsset === chain.native.asset
        ? chain.native.wrapped
        : chain.nativeTokenSupported && this.inputLendAsset === chain.native.wrapped
        ? chain.native.asset
        : this.inputLendAsset

    const lendItem = lendStore.lendItems.find((item) => item.asset === searchAsset)
    if (lendItem) {
      const rate = this.nextSupplyRate || lendItem.interestRate
      const yearlyProfit = rate.lt(0.0001)
        ? new BigNumber(0)
        : rate.div(100).times(this.inputLendAmount || 0) // default to 0 if the user empties the field
      return {
        lendItem,
        profitEstimates: {
          yearly: yearlyProfit,
          monthly: yearlyProfit.div(12),
          weekly: yearlyProfit.div(52),
        },
      }
    }
  }

  get canSubmit() {
    const inputAmount = new BigNumber(this.inputLendAmount)
    return inputAmount.gt(0) && inputAmount.lte(this.maxLendAmount)
  }

  get wrapNativeFormVisible() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return false
    }
    const { native, nativeTokenSupported } = chainConfigs[chainId]
    return !nativeTokenSupported && this.inputLendAsset === native.asset
  }

  get needsApproval() {
    const { web3Connection, spendingApprovals } = this.rootStore
    const { chainId } = web3Connection
    if (
      !chainId ||
      this.lendType === LendType.UNLEND ||
      this.lendAmountAsBN.isNaN() ||
      !this.lendAmountAsBN.gt(0) ||
      this.lendAmountAsBN.gt(this.maxLendAmount)
    ) {
      // We return that it does not need approval if input is bigger than balance
      // to avoid showing approval button
      return false
    }
    return spendingApprovals.needsApproval(
      chainId,
      this.inputLendAsset,
      assetUtils.getITokenAddress(chainId, this.inputLendAsset),
      this.lendAmountAsBN
    )
  }

  set(prop: keyof ILendForm, value: any) {
    ;(this[prop] as any) = value
  }

  assign(props: Partial<ILendForm>) {
    Object.assign(this, props)
  }

  /**
   * TODO: use framework inputs later for inputAmount component
   */
  changeAmount(value: string) {
    this.inputLendAmount = value
  }

  setMax() {
    this.inputLendAmount = numberUtils.formatPrecision(this.maxLendAmount)
  }

  /**
   * @param percent percentage of maxLendAmount
   */
  setAmountFromMaxPercent(percent: number) {
    this.inputLendAmount = numberUtils.formatPrecision(this.maxLendAmount.times(percent).toNumber())
  }

  changeCollateral(asset: Asset) {
    this.inputLendAsset = asset
    this.getData()
  }

  submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const { lendStore } = this.rootStore
    let amount = new BigNumber(this.inputLendAmount)
    const inputAmountRounded = numberUtils.formatPrecision(this.inputLendAmount)
    const maxLendAmountRounded = numberUtils.formatPrecision(this.maxLendAmount)

    if (inputAmountRounded === maxLendAmountRounded) {
      amount = new BigNumber(this.maxLendAmount)
    }

    this.lendDialog.hide()

    setTimeout(() => {
      if (this.lendType === 'LEND') {
        lendStore.lend(this.inputLendAsset, amount)
      } else {
        lendStore.unlend(this.inputLendAsset, amount.times(10 ** 18).div(this.tokenPrice))
      }
    }, 0)
  }

  async approveSpending() {
    const { spendingApprovals } = this.rootStore
    this.assign({ spendingApprovalPending: true })
    await spendingApprovals.setInfiniteApprovalForIToken(this.inputLendAsset)
    this.assign({ spendingApprovalPending: false })
  }

  async getData() {
    const { fulcrumProvider, web3Connection } = this.rootStore
    const { chainId } = web3Connection
    if (!chainId) {
      return
    }

    // Check token approval
    if (this.lendType === 'LEND') {
      await this.rootStore.spendingApprovals.checkApproval(
        this.inputLendAsset,
        assetUtils.getITokenAddress(chainId, this.inputLendAsset)
      )
    }
    this.assign({
      iTokenAddress: assetUtils.getITokenAddress(chainId, this.inputLendAsset),
    })

    const maxLendValue = await lendUtils.getMaxLendValue(
      fulcrumProvider,
      this.lendType,
      this.inputLendAsset
    )
    this.assign(maxLendValue)
    this.set('inputLendAmount', numberUtils.formatPrecision(maxLendValue.maxLendAmount))

    if (this.wrapNativeFormVisible) {
      this.wrapNativeForm.syncData()
    }
  }

  useWrappedNative() {
    const { chainId } = this.rootStore.web3Connection
    chainId && this.changeCollateral(chainConfigs[chainId].native.wrapped)
  }

  async refreshSupplyRate(supplyAmount: BigNumber) {
    const { chain, fulcrumProvider } = this.rootStore
    if (!chain || supplyAmount.isNaN()) {
      return
    }
    try {
      const nextSupplyRate = await lendUtils.nextSupplyInterestRate(
        fulcrumProvider,
        this.inputLendAsset,
        this.lendAmountAsBN.times(this.isLending ? 1 : -1)
      )
      this.assign({ nextSupplyRate })
    } catch (err) {
      // fail silently for now; TODO: discuss how to handle error on the UI
      console.error(err)
    }
  }

  async init() {
    const { web3Connection } = this.rootStore
    const { chainId } = web3Connection
    if (!chainId) {
      return
    }
    try {
      await this.getData()
    } catch (err) {
      console.error(err)
    } finally {
      this.set('isLoading', false)
    }

    this.stopAutoUpdate = mobx.autorun(() => {
      this.refreshSupplyRateDebounced(this.lendAmountAsBN)
    })
  }

  destroyVM() {
    this.stopAutoUpdate && this.stopAutoUpdate()
  }

  constructor(asset: Asset, lendType: LendType, rootStore: RootStore, lendDialog: DialogVM) {
    this.lendType = lendType
    this.rootStore = rootStore
    this.inputLendAsset = asset
    this.lendDialog = lendDialog
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
    this.wrapNativeForm = new WrapNativeFormVM(this.rootStore, {
      useWrapped: this.useWrappedNative,
      directionAllowed: 'wrap',
    })
    this.refreshSupplyRateDebounced = debounce(
      (supplyAmount: BigNumber) => this.refreshSupplyRate(supplyAmount),
      250
    )
  }
}
